import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const SUPPORT_EMAIL = "support.idevorai@gmail.com";
const POLL_MS = 3000;
const MAX_MS = 60000;

export default function Bienvenue() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | timeout
  const redirectRef = useRef(false);

  const payment_id = params.get("payment_id") || params.get("payment") || "";
  const receipt_id = params.get("receipt_id") || params.get("receipt") || "";
  const membership_id = params.get("membership_id") || params.get("membership") || "";

  useEffect(() => {
    let active = true;
    const start = Date.now();

    const checkOnce = async () => {
      // 1) Server-side verification of the Whop redirect params (never trusts the URL alone).
      if (payment_id || receipt_id || membership_id) {
        try {
          const { data } = await api.get("/whop/verify-checkout", { params: { payment_id, receipt_id, membership_id } });
          if (active && data?.state === "paid") { setStatus("paid"); if (refresh) refresh(); return true; }
          if (active && data?.state === "pending") { setStatus("pending"); return true; }
        } catch (e) { /* keep trying */ }
      }
      // 2) Our own source of truth (set by the Whop webhook) for same-browser logged-in buyers.
      try {
        const { data } = await api.get("/subscription");
        if (active && data?.has_subscription) { setStatus("paid"); if (refresh) refresh(); return true; }
      } catch (e) { /* not logged in yet */ }
      return false;
    };

    const tick = async () => {
      if (!active) return;
      const done = await checkOnce();
      if (done || !active) return;
      if (Date.now() - start >= MAX_MS) { setStatus("timeout"); return; }
      timer = setTimeout(tick, POLL_MS);
    };
    let timer = setTimeout(tick, 0);
    return () => { active = false; clearTimeout(timer); };
  }, [payment_id, receipt_id, membership_id, refresh]);

  useEffect(() => {
    if (status === "paid" && !redirectRef.current) {
      redirectRef.current = true;
      const t = setTimeout(() => navigate("/compte"), 3000);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container page-content fade-in" data-testid="bienvenue-page">
        <div style={{ maxWidth: 580, margin: "0 auto", textAlign: "center", paddingTop: "4vh" }}>
          {status === "pending" ? (
            <div data-testid="bienvenue-pending">
              <div style={{ fontSize: 52, lineHeight: 1 }} aria-hidden="true">⏳</div>
              <h1 className="mt-6">Paiement en cours de validation</h1>
              <p className="muted mt-6 text-lg">Ton paiement est en cours de validation par ta banque. Ton business sera créé dès sa confirmation.</p>
              <button onClick={() => navigate("/compte")} data-testid="bienvenue-access-btn" className="btn-primary mt-8">Accéder à mon espace <span aria-hidden="true">→</span></button>
              <p className="muted text-sm mt-6">Besoin d'aide ? <a className="accent underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
            </div>
          ) : status === "timeout" ? (
            <div data-testid="bienvenue-timeout">
              <div style={{ fontSize: 52, lineHeight: 1 }} aria-hidden="true">✅</div>
              <h1 className="mt-6">Paiement confirmé</h1>
              <p className="mt-6 text-lg">Connecte-toi avec l'email de ton paiement</p>
              <p className="muted mt-3">Ton accès s'active en quelques instants. Connecte-toi avec l'adresse e-mail utilisée lors du paiement pour retrouver ton espace.</p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
                <button onClick={() => navigate("/compte")} data-testid="bienvenue-access-btn" className="btn-primary">Accéder à mon espace <span aria-hidden="true">→</span></button>
                <button onClick={() => navigate("/retrouver")} data-testid="bienvenue-login-btn" className="btn-secondary">Me connecter</button>
              </div>
              <p className="muted text-sm mt-6">Besoin d'aide ? <a className="accent underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
            </div>
          ) : (
            <div data-testid="bienvenue-checking">
              <div style={{ fontSize: 56, lineHeight: 1 }} aria-hidden="true">✅</div>
              <h1 className="mt-6" data-testid="bienvenue-title">Paiement confirmé</h1>
              <p className="muted mt-6 text-lg" data-testid="bienvenue-status">{status === "paid" ? "Ton espace est prêt." : "Préparation de ton espace…"}</p>
              {status === "paid" ? (
                <p className="muted text-sm mt-2">Redirection automatique vers ton espace…</p>
              ) : (
                <div className="load40-bar mt-6" style={{ maxWidth: 260, margin: "1.5rem auto 0" }}><div className="load40-bar-fill" style={{ width: "60%" }} /></div>
              )}
              <button onClick={() => navigate("/compte")} data-testid="bienvenue-access-btn" className="btn-primary mt-8">Accéder à mon espace <span aria-hidden="true">→</span></button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
